import React, { useState, useEffect } from 'react';
import HeatmapDisplay from './HeatmapDisplay';
import styled from 'styled-components';
import { blackScholesApi } from '../../services/api';
import { useParams, useNavigate } from 'react-router-dom';
import InputForm from './InputForm';
import ResultsDisplay from './ResultsDisplay';
import TickerSearch from './TickerSearch';

const Container = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  min-height: calc(100vh - 60px);
  max-width: 1400px;
  margin: 0 auto;
  padding: 1rem;
  gap: 1.5rem;
  overflow-x: hidden;
  background-color: var(--bg-color);
  color: var(--text-color);
  transition: background-color 0.3s ease, color 0.3s ease;

  @media (max-width: 1200px) {
    grid-template-columns: 280px 1fr;
    padding: 0.75rem;
    gap: 1rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0.5rem;
    margin: 0;
    width: 100%;
    gap: 0.75rem;
  }
`;

const Sidebar = styled.div`
  background-color: var(--sidebar-bg);
  padding: 1.25rem;
  border-radius: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  height: fit-content;
  border: 1px solid var(--sidebar-border);
  position: sticky;
  top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  color: var(--text-color);
  transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;

  @media (max-width: 1200px) {
    padding: 1rem;
  }

  @media (max-width: 768px) {
    position: static;
    border-radius: 8px;
    padding: 1rem;
    margin: 0;
  }
`;

const Form = styled.form`
  background-color: var(--card-bg-color);
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  max-width: 500px;
  margin: 0 auto;
  color: var(--text-color);
  transition: background-color 0.3s ease, color 0.3s ease;

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 0;
    box-shadow: none;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  @media (max-width: 1200px) {
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const Title = styled.h1`
  margin: 0 0 1.5rem 0;
  color: var(--text-color);
  font-size: 1.75rem;
  transition: color 0.3s ease;

  @media (max-width: 1200px) {
    font-size: 1.5rem;
    margin-bottom: 1.25rem;
  }

  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 1rem;
  }
`;

const ResultsGrid = styled.div`
  width: 100%;
  background: var(--card-bg-color);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 1.5rem;
  color: var(--text-color);
  transition: background-color 0.3s ease, color 0.3s ease;

  @media (max-width: 1200px) {
    padding: 1.25rem;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 6px;
  }
`;

const BlackScholesCalculator = () => {
  const params = useParams();
  const navigate = useNavigate();
  
  const defaultInputs = {
    stock_price: 100,
    strike_price: 100,
    time_to_expiry: 30,
    risk_free_rate: 0.05,
    volatility: 0.2,
    time_in_days: true,
    ticker: ''
  };
  
  const [inputs, setInputs] = useState(defaultInputs);
  const [results, setResults] = useState(null);
  const [greeks, setGreeks] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [heatmapData, setHeatmapData] = useState(null);
  const [tickerError, setTickerError] = useState('');
  
  useEffect(() => {
    // Check for ticker parameter in URL query
    const searchParams = new URLSearchParams(window.location.search);
    const queryTickerParam = searchParams.get('ticker');
    
    if (queryTickerParam) {
      const tickerSymbol = queryTickerParam.toUpperCase();
      handleTickerSubmit(tickerSymbol);
    }
  }, []);
  
  useEffect(() => {
    calculateOptionPrice();
  }, [inputs]);
  
  const handleTickerSubmit = async (tickerSymbol) => {
    try {
      setTickerError('');
      setIsLoading(true);
      const response = await blackScholesApi.getTickerPrice(tickerSymbol);
      
      if (response && response.price !== null && response.found) {
        // Successfully found ticker price - update stock price and clear any error
        setInputs(prevInputs => ({
          ...prevInputs,
          stock_price: response.price,
          ticker: tickerSymbol
        }));
        
        // Update URL with the ticker as a query parameter
        navigate(`/?ticker=${tickerSymbol}`, { replace: true });
        
        // Clear any error message
        setTickerError('');
      } else {
        // Only set error if the ticker wasn't found
        setTickerError('Ticker not found');
      }
    } catch (error) {
      console.error('Error fetching ticker data:', error);
      setTickerError('Ticker not found');
    } finally {
      setIsLoading(false);
    }
  };
  
  const calculateOptionPrice = async () => {
    try {
      setIsLoading(true);
      
      const response = await blackScholesApi.calculateOptions(inputs);
      
      setResults({
        call_price: response.call_price,
        put_price: response.put_price
      });
      
      setGreeks({
        call: response.call_greeks,
        put: response.put_greeks
      });
    } catch (error) {
      console.error('Error in Black-Scholes calculation:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleInputChange = (name, value) => {
    setInputs({
      ...inputs,
      [name]: value
    });
  };
  

  const handleReset = () => {
    setInputs(defaultInputs);
  };
  
  useEffect(() => {
    generateHeatmapData();
  }, [inputs]);

  const generateHeatmapData = async () => {
    try {
      const data = await blackScholesApi.generateHeatmap(inputs);
      setHeatmapData(data);
    } catch (error) {
      console.error('Error generating heatmap data:', error);
    }
  };

  return (
    <Container>
      <Sidebar>
        <Title>Input Parameters</Title>
        <TickerSearch 
          onTickerSubmit={handleTickerSubmit}
          initialTicker={inputs.ticker}
          isLoading={isLoading}
          error={tickerError}
        />
        <InputForm
          inputs={inputs}
          onInputChange={handleInputChange}
          onReset={handleReset}
          isLoading={isLoading}
        />
      </Sidebar>
      
      <MainContent>
        <ResultsGrid>
          <ResultsDisplay
            results={results}
            greeks={greeks}
            isLoading={isLoading}
          />
        </ResultsGrid>
        
        <HeatmapDisplay
          heatmapData={heatmapData}
          isLoading={isLoading}
        />
      </MainContent>
    </Container>
  );
};

export default BlackScholesCalculator;
